import { MutationFunctionOptions, useMutation, useQuery } from '@apollo/client';
import { Store } from '@website/Store';
import MediaSelectorField from '@website/components/forms/fields/MediaSelectorField';
import PhoneNoInput from '@website/components/forms/fields/PhoneNoInput';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import Link from 'next/link';
import { NextRouter, useRouter } from 'next/router';
import { useContext, useEffect } from 'react';
import { Button, Col, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import {
  Media,
  UpdateUserDocument,
  UpdateUserInput,
  UpdateUserMutation,
  UserForUpdateDocument,
  UserForUpdateQuery,
} from '../../../types/graphqlOperations';
import ErrorPage from '../../utilities/ErrorPage';
import LoadingPage from '../../utilities/LoadingPage';

/**
 * Format Submit Values
 *
 * As we want to only update some profile fields (there are others e.g. address that we don't update here), we want to
 * keep any unchanged values from the initial user query and then just add in the updated values.
 */
export function formatSubmitValues(
  values: UpdateUserInput,
  user: UserForUpdateQuery['user'],
): UpdateUserInput {
  // Combine initial values with any changes from the new ones
  const input = { ...user, ...values } as UpdateUserInput;
  // eslint-disable-next-line no-underscore-dangle
  delete (input as any).__typename; // Delete the __typename field from Apollo caching

  // Handle an empty birthDate field (html date input returns '' but the API requires null)
  if (input.birthDate === '' || !input.birthDate) delete input.birthDate;

  // Handle no gender
  if (!input.gender) delete input.gender;

  if (input.picture) {
    input.picture = {
      id: input.picture.id,
    };
  }

  return input;
}

/**
 * On submit function (for updateUser)
 */
export async function onSubmit(
  values: UpdateUserInput,
  user: UserForUpdateQuery['user'],
  updateUserMutation: (
    options?: MutationFunctionOptions<
      UpdateUserMutation,
      { input: UpdateUserInput }
    >,
  ) => Promise<any>,
  router: NextRouter,
) {
  try {
    const input = formatSubmitValues(values, user);

    await updateUserMutation({
      variables: { input },
    });

    // redirect to the user's profile page
    router.push(`/me/${user.id}`);
  } catch (e) {
    alert(e);
  }
}

/**
 * Update User Form Inner
 *
 * Note we only include the key fields here, which things like the user address not added here.
 */
export function UpdateUserFormInner({
  user,
}: {
  user: UserForUpdateQuery['user'];
}) {
  // Get the user country
  const { state } = useContext(Store);
  const router = useRouter();

  // Setup the mutation
  const [updateUserMutation] = useMutation(UpdateUserDocument);

  // Setup react-hook-form
  const {
    handleSubmit,
    register,
    control,
    formState: { isSubmitting },
    setError,
    clearErrors,
    trigger,
  } = useForm<Omit<UpdateUserInput, 'picture'> & { picture: Media }>({
    defaultValues: {
      ...user,
      phone: user.phone || undefined,
    },
    shouldFocusError: true,
  });

  // Trigger validation on email and phone fields if they are already filled in
  // This is to ensure that the validation errors are shown if the user has
  // already filled in these fields before the form is submitted
  useEffect(() => {
    if (user.email) {
      trigger('email');
    }
    if (user.phone) {
      trigger('phone');
    }
  }, [trigger, user.email, user.phone]);

  const isAdmin = state.user.roles.includes('admin');

  return (
    <Form
      onSubmit={handleSubmit(async (values) =>
        onSubmit(values, user, updateUserMutation, router),
      )}
    >
      <Col className="mt-3 mb-3">
        <MediaSelectorField
          name="picture"
          control={control}
          userID={user.id}
          selectButtonLabel={
            user.picture ? 'Change Profile Picture' : 'Add Profile Picture'
          }
          hidePreview
          avatarDefaultImageText={`${user.givenName} ${user.familyName}`}
          showAvatar
        />
      </Col>

      <Form.Group controlId="givenName">
        <Form.Label>First name</Form.Label>
        <Form.Control
          maxLength={35}
          minLength={2}
          required
          {...register('givenName')}
          type="text"
        />
      </Form.Group>

      <Form.Group controlId="familyName">
        <Form.Label>Last name</Form.Label>
        <Form.Control
          maxLength={35}
          minLength={2}
          required
          {...register('familyName')}
          type="text"
        />
      </Form.Group>

      {isAdmin && (
        <Form.Group controlId="email">
          <Form.Label>Email</Form.Label>
          <Form.Control
            maxLength={35}
            minLength={2}
            required
            {...register('email')}
            type="email"
          />
        </Form.Group>
      )}

      <Form.Group controlId="phone">
        <Form.Label>Phone number</Form.Label>
        <PhoneNoInput
          name="phone"
          control={control}
          setError={setError}
          clearErrors={clearErrors}
          required
        />
      </Form.Group>

      <Form.Group controlId="birthDate">
        <Form.Label>Birthday</Form.Label>
        <Form.Control {...register('birthDate')} type="date" />
      </Form.Group>

      <Form.Group controlId="bio">
        <Form.Label>About you</Form.Label>
        <Form.Control {...register('bio')} as="textarea" rows={3} />
        <Form.Text>
          Tell us a bit about you. Other guests on your trips will be able to
          see this.
        </Form.Text>
      </Form.Group>
      <br />
      <SubmitButton isSubmitting={isSubmitting} block />
      <Link href={`/me/${user.id}`} legacyBehavior>
        <div className="d-grid gap-2">
          <Button className="mt-2" variant="danger">
            Cancel
          </Button>
        </div>
      </Link>
    </Form>
  );
}

/**
 * Update User Form
 */
export default function UpdateUserForm({ userID }) {
  // Get the user
  const { loading, data, error } = useQuery(UserForUpdateDocument, {
    variables: { id: userID },
    fetchPolicy: 'cache-and-network',
  });

  // Handle loading/error
  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage />;

  // Return form
  return <UpdateUserFormInner user={data.user as UserForUpdateQuery['user']} />;
}
